import type { ISODateString, UUID } from './common';

/**
 * Methods a user may choose. `external` is deliberately NOT here: it marks
 * records migrated from QuickBooks and is never offered in the UI. Declaring
 * the selectable set first lets the Zod schema be built from it directly, so
 * "external is not selectable" is a type-level fact rather than a refinement
 * every caller has to remember.
 */
// NOTE: the stored value stays `cheque` — it is a Postgres enum, and renaming
// it would be a migration plus a data rewrite for a word nobody sees. The LABEL
// below is what the office reads, and that is American: "Check".
export const SELECTABLE_PAYMENT_METHODS = ['cheque', 'ach', 'card', 'cash'] as const;
export type SelectablePaymentMethod = (typeof SELECTABLE_PAYMENT_METHODS)[number];

export const PAYMENT_METHODS = [...SELECTABLE_PAYMENT_METHODS, 'external'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * NOTE (FR-1.2): `cash` here is a payment METHOD — an ordinary invoice settled
 * with banknotes. It is NOT `invoices.isCash`, which marks a cash INVOICE on the
 * separate INV-C<n> sequence rendered as a "Cash adjustment" on finals. Never
 * present the two in the same control.
 */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cheque: 'Check',
  ach: 'ACH / wire',
  card: 'Card',
  cash: 'Cash',
  external: 'Recorded outside jobOS',
};

export const PAYMENT_STATES = ['active', 'superseded', 'reversed'] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];

export interface InvoicePayment {
  id: UUID;
  invoiceId: UUID;
  amount: number;
  receivedOn: string;
  method: PaymentMethod;
  reference: string | null;
  state: PaymentState;
  supersedesId: UUID | null;
  reversesId: UUID | null;
  reversalReason: string | null;
  createdBy: UUID | null;
  createdByName: string | null;
  createdAt: ISODateString;
}
